<template>
  <div class="container-fluid h-100 p-0">
    <div class="row h-100 g-0">
      <div class="col-12 h-100">
        <div
          class="overflow-y-auto h-100 pt-4 px-3"
          style="height: calc(100vh - 60px)"
        >
          <!-- Main Title -->
          <h3 class="mb-3">{{ $t("project.consumption") }}</h3>

          <!-- Main Card containing all sections -->
          <div class="card main-card border-0">
            <div class="card-body">
              <!-- Consumption Type Selector -->
              <div class="mb-4">
                <div class="btn-group w-100" role="group">
                  <input
                    type="radio"
                    class="btn-check"
                    name="consumptionType"
                    id="yearly"
                    value="yearly"
                    v-model="consumptionType"
                  />
                  <label class="btn btn-outline-primary" for="yearly">{{
                    $t("project.consumptionComponent.yearly")
                  }}</label>

                  <input
                    type="radio"
                    class="btn-check"
                    name="consumptionType"
                    id="monthly"
                    value="monthly"
                    v-model="consumptionType"
                  />
                  <label class="btn btn-outline-primary" for="monthly">{{
                    $t("project.consumptionComponent.monthly")
                  }}</label>
                </div>
              </div>

              <!-- Energy Price Section -->
              <div class="mb-5">
                <h4 class="mb-3">
                  {{ $t("project.consumptionComponent.energyPrice") }}
                </h4>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">{{
                      $t("project.consumptionComponent.importPrice")
                    }}</label>
                    <div class="input-group">
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="energyPrices.import"
                        min="0"
                        step="0.01"
                      />
                      <span class="input-group-text">€/kWh</span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">{{
                      $t("project.consumptionComponent.exportPrice")
                    }}</label>
                    <div class="input-group">
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="energyPrices.export"
                        min="0"
                        step="0.01"
                      />
                      <span class="input-group-text">€/kWh</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Yearly Input -->
              <div v-if="consumptionType === 'yearly'" class="mb-5">
                <h4 class="mb-3">
                  {{ $t("project.consumptionComponent.yearlyConsumption") }}
                </h4>
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">F1</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="yearlyConsumption.F1"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">F2</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="yearlyConsumption.F2"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">F3</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="yearlyConsumption.F3"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <!-- Monthly Input -->
              <div v-if="consumptionType === 'monthly'" class="mb-4">
                <h4 class="mb-3">
                  {{ $t("project.consumptionComponent.monthlyConsumption") }}
                </h4>
                <div class="table-responsive">
                  <table class="table table-bordered">
                    <thead>
                      <tr>
                        <th>{{ $t("project.consumptionComponent.month") }}</th>
                        <th>F1</th>
                        <th>F2</th>
                        <th>F3</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(month, index) in months" :key="index">
                        <td>
                          {{
                            $t(
                              `project.consumptionComponent.months.${monthKeys[index]}`
                            )
                          }}
                        </td>
                        <td>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model.number="monthlyConsumption[index].F1"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model.number="monthlyConsumption[index].F2"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model.number="monthlyConsumption[index].F3"
                            min="0"
                            step="0.01"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Total Consumption Summary -->
              <div class="mb-4">
                <div class="card total-card">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-12">
                        <div class="fw-bold">
                          {{ $t("project.consumptionComponent.grandTotal") }}
                        </div>
                        <div class="total-value">
                          {{ grandTotal.toFixed(2) }} kWh
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useProjectStore } from "@/stores/project";
import { storeToRefs } from "pinia";
import type { ConsumptionData as ProjectConsumptionData } from "@/types/projectTypes";
import { useI18n } from "vue-i18n";

interface ConsumptionData {
  F1: number;
  F2: number;
  F3: number;
}

export default defineComponent({
  name: "ProjectConsumptionComponent",
  components: {},
  setup() {
    const { t } = useI18n();
    const projectStore = useProjectStore();
    const { project } = storeToRefs(projectStore);

    const consumptionType = ref<"yearly" | "monthly">("yearly");
    // Use translation keys instead of hardcoded month names
    const monthKeys = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];

    const months = monthKeys.map((key) =>
      t(`project.consumptionComponent.months.${key}`)
    );

    // Yearly consumption data
    const yearlyConsumption = ref<ConsumptionData>({
      F1: 0,
      F2: 0,
      F3: 0,
    });

    // Monthly consumption data
    const monthlyConsumption = ref<ConsumptionData[]>(
      monthKeys.map(() => ({ F1: 0, F2: 0, F3: 0 }))
    );

    // Energy price data
    const energyPrices = ref({
      import: 0.22,
      export: 0.1,
    });

    // Calculate totals
    const totalF1 = computed(() => {
      if (consumptionType.value === "yearly") {
        return Number(yearlyConsumption.value.F1 || 0);
      }
      return monthlyConsumption.value.reduce(
        (sum, month) => sum + Number(month.F1 || 0),
        0
      );
    });

    const totalF2 = computed(() => {
      if (consumptionType.value === "yearly") {
        return Number(yearlyConsumption.value.F2 || 0);
      }
      return monthlyConsumption.value.reduce(
        (sum, month) => sum + Number(month.F2 || 0),
        0
      );
    });

    const totalF3 = computed(() => {
      if (consumptionType.value === "yearly") {
        return Number(yearlyConsumption.value.F3 || 0);
      }
      return monthlyConsumption.value.reduce(
        (sum, month) => sum + Number(month.F3 || 0),
        0
      );
    });

    const grandTotal = computed(
      () => totalF1.value + totalF2.value + totalF3.value
    );

    // Flag to prevent watcher from firing during initialization
    const isInitialized = ref(false);

    // Initialize from existing project data if available
    onMounted(() => {
      if (project.value?.consumption_data) {
        // Check if we have data arrays
        if (
          Array.isArray(project.value.consumption_data.f1_data) &&
          project.value.consumption_data.f1_data.length > 0
        ) {
          // Use the isYearlyData flag to determine the consumption type
          consumptionType.value = project.value.consumption_data.is_yearly_data
            ? "yearly"
            : "monthly";

          if (consumptionType.value === "yearly") {
            // If yearly data, use the single value
            yearlyConsumption.value = {
              F1: project.value.consumption_data.f1_data[0] || 0,
              F2: project.value.consumption_data.f2_data[0] || 0,
              F3: project.value.consumption_data.f3_data[0] || 0,
            };
          } else {
            // If monthly data, use the values directly
            for (let i = 0; i < 12; i++) {
              monthlyConsumption.value[i] = {
                F1: project.value.consumption_data.f1_data[i] || 0,
                F2: project.value.consumption_data.f2_data[i] || 0,
                F3: project.value.consumption_data.f3_data[i] || 0,
              };
            }
          }
        } else {
          // If we don't have data, initialize yearly totals
          consumptionType.value = "yearly";
          yearlyConsumption.value = {
            F1: project.value.consumption_data.grand_total / 3 || 0,
            F2: project.value.consumption_data.grand_total / 3 || 0,
            F3: project.value.consumption_data.grand_total / 3 || 0,
          };
        }
      } else {
        // If no consumption data exists, initialize with zeros
        consumptionType.value = "yearly";
        yearlyConsumption.value = { F1: 0, F2: 0, F3: 0 };
        monthlyConsumption.value = monthKeys.map(() => ({
          F1: 0,
          F2: 0,
          F3: 0,
        }));
      }

      // Initialize energy prices from project store if available
      console.log("[Initialization] Setting up energy prices");
      console.log("[Initialization] Project financial_data:", project.value?.financial_data);
      
      if (project.value?.financial_data) {
        const savedImport = project.value.financial_data.import_energy_price;
        const savedExport = project.value.financial_data.export_energy_price;
        
        console.log("[Initialization] Saved values - import:", savedImport, "export:", savedExport);
        
        energyPrices.value.import = savedImport || 0.22;
        energyPrices.value.export = savedExport || 0.1;
        
        console.log("[Initialization] Set values - import:", energyPrices.value.import, "export:", energyPrices.value.export);
      } else {
        // Ensure default values are set if no financial_data exists
        console.log("[Initialization] No financial_data, setting defaults");
        energyPrices.value.import = 0.22;
        energyPrices.value.export = 0.1;
        console.log("[Initialization] Set default values - import:", energyPrices.value.import, "export:", energyPrices.value.export);
      }

      // Mark as initialized after setting up initial values
      console.log("[Initialization] Setting isInitialized to true");
      isInitialized.value = true;
      
      // Manually trigger the energy prices watcher to ensure default values are saved
      console.log("[Initialization] Manually triggering energy prices save");
      
      // Ensure financial_data exists and save default energy prices
      if (project.value) {
        if (!project.value.financial_data) {
          console.log("[Initialization] Creating financial_data");
          projectStore.$patch((state) => {
            if (state.project) {
              state.project.financial_data = {
                payment_type: "one_time",
                installment_ratio: 0,
                installment_duration_months: 120,
                import_energy_price: 0,
                export_energy_price: 0,
                annual_revenue: 0,
                base_price: 0,
                discount_amount: 0,
                discount_percentage: 0,
                cashback_amount: 0,
                cashback_percentage: 0,
                final_price: 0,
                installment_amount: 0,
                total_installment_amount: 0,
                payment_schedule: [],
                currency: "EUR",
                vat_percentage: 0,
                vat_amount: 0,
                include_renew_inverter: false,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
            }
          });
        }
        
        // Save the current energy prices to the store
        console.log("[Initialization] Saving energy prices to store:", energyPrices.value);
        projectStore.$patch((state) => {
          if (state.project && state.project.financial_data) {
            state.project.financial_data.import_energy_price = Number(energyPrices.value.import);
            state.project.financial_data.export_energy_price = Number(energyPrices.value.export);
          }
        });
      }
    });

    // Set up the watcher outside of onMounted to avoid immediate firing
    watch(
      [consumptionType, yearlyConsumption, monthlyConsumption],
      () => {
        // Only save if component is initialized and project exists
        if (!isInitialized.value || !project.value) {
          return;
        }

        let F1Array: number[] = [];
        let F2Array: number[] = [];
        let F3Array: number[] = [];

        if (consumptionType.value === "monthly") {
          // Fill arrays with monthly data
          F1Array = monthlyConsumption.value.map(
            (month) => Number(month.F1) || 0
          );
          F2Array = monthlyConsumption.value.map(
            (month) => Number(month.F2) || 0
          );
          F3Array = monthlyConsumption.value.map(
            (month) => Number(month.F3) || 0
          );
        } else {
          // For yearly data, save only one value per phase
          F1Array = [Number(yearlyConsumption.value.F1) || 0];
          F2Array = [Number(yearlyConsumption.value.F2) || 0];
          F3Array = [Number(yearlyConsumption.value.F3) || 0];
        }

        // Update project store with consumption data
        const newConsumptionData: ProjectConsumptionData = {
          f1_data: F1Array,
          f2_data: F2Array,
          f3_data: F3Array,
          grand_total: grandTotal.value,
          is_yearly_data: consumptionType.value === "yearly",
        };

        projectStore.setConsumptionData(newConsumptionData);
      },
      { deep: true }
    );

    // Add watcher for energy prices
    watch(
      energyPrices,
      () => {
        console.log("[EnergyPrices Watcher] Fired with values:", energyPrices.value);
        console.log("[EnergyPrices Watcher] isInitialized:", isInitialized.value);
        console.log("[EnergyPrices Watcher] project exists:", !!project.value);
        
        // Only save if component is initialized and project exists
        if (!isInitialized.value || !project.value) {
          console.log("[EnergyPrices Watcher] Skipping - not initialized or no project");
          return;
        }

        // Ensure financial_data exists
        if (!project.value.financial_data) {
          console.log("[EnergyPrices Watcher] Creating financial_data");
          projectStore.$patch((state) => {
            if (state.project) {
              state.project.financial_data = {
                payment_type: "one_time",
                installment_ratio: 0,
                installment_duration_months: 120,
                import_energy_price: 0,
                export_energy_price: 0,
                annual_revenue: 0,
                base_price: 0,
                discount_amount: 0,
                discount_percentage: 0,
                cashback_amount: 0,
                cashback_percentage: 0,
                final_price: 0,
                installment_amount: 0,
                total_installment_amount: 0,
                payment_schedule: [],
                currency: "EUR",
                vat_percentage: 0,
                vat_amount: 0,
                include_renew_inverter: false,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
            }
          });
        }

        // Update energy prices in project store
        const importPrice = Number(energyPrices.value.import);
        const exportPrice = Number(energyPrices.value.export);
        
        console.log("[EnergyPrices Watcher] Saving to store - import:", importPrice, "export:", exportPrice);
        
        projectStore.$patch((state) => {
          if (state.project && state.project.financial_data) {
            state.project.financial_data.import_energy_price = importPrice;
            state.project.financial_data.export_energy_price = exportPrice;
          }
        });
      },
      { deep: true }
    );

    return {
      consumptionType,
      months,
      monthKeys,
      yearlyConsumption,
      monthlyConsumption,
      energyPrices,
      totalF1,
      totalF2,
      totalF3,
      grandTotal,
    };
  },
});
</script>

<style scoped>
.form-control {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.form-control:focus {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.table input {
  min-width: 80px;
  border-radius: 2px;
}

.main-card {
  box-shadow: none;
  padding: 0 100px;
  border-radius: 2px;
}

.btn-group {
  box-shadow: none;
}

.btn-group .btn {
  border: none;
  border-bottom: 1px solid var(--color-light-blue);
  color: var(--color-dark);
  background: transparent;
  padding: 8px 16px;
  font-weight: 500;
  border-radius: 0;
}

.btn-check:checked + .btn-outline-primary {
  background-color: transparent;
  border: none;
  border-bottom: 3px solid var(--primary-color);
  color: var(--primary-color);
  border-radius: 0;
}

.btn-outline-primary {
  border: none;
  border-bottom: 1px solid var(--color-light-blue);
  color: var(--color-dark);
  background: transparent;
  border-radius: 0;
}

.btn-outline-primary:hover {
  background-color: transparent;
  border: none;
  color: var(--primary-color);
}

.input-group-text {
  border-radius: 2px;
}

.table {
  border-radius: 2px;
}

.table th,
.table td {
  border-radius: 2px;
}

.total-card {
  background-color: var(--color-light-green);
  border: none;
  border-radius: 2px;
  padding: 0 50px;
}

.total-value {
  font-size: 1.25rem;
  color: var(--color-green);
  margin-top: 0.5rem;
  font-weight: bold;
}
</style>
